import React, { useEffect, useState } from 'react';
import { Badge } from './ui/badge';
import { UserCheck, X } from 'lucide-react';

/**
 * iter109 — CustomerDedupHints
 *
 * When a user types a freeform customer name on an SO/WO, debounce-fetch
 * near-match customers and surface them as "did you mean?" chips.
 *
 * Props:
 *   api        — () => axios instance
 *   name       — the typed name (freeform)
 *   onPick     — (customer) => void; called when the user picks a chip
 *   skip       — bool; disable (e.g. once a customer_id is set)
 *   testIdPrefix
 */
const CustomerDedupHints = ({ api, name, onPick, skip = false, testIdPrefix = 'dedup' }) => {
  const [hints, setHints] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (skip) { setHints([]); return; }
    const raw = (name || '').trim();
    if (raw.length < 2) { setHints([]); return; }
    setDismissed(false);
    const t = setTimeout(async () => {
      try {
        const r = await api().get(`/customers/dedup-hints?name=${encodeURIComponent(raw)}&limit=5`);
        setHints(r.data || []);
      } catch { /* non-blocking */ }
    }, 350);
    return () => clearTimeout(t);
  }, [api, name, skip]);

  if (skip || dismissed || hints.length === 0) return null;

  return (
    <div className="text-[11px] text-slate-600 flex items-center gap-1 flex-wrap mt-1" data-testid={`${testIdPrefix}-root`}>
      <UserCheck className="w-3 h-3 text-sky-600" />
      <span className="font-medium">Did you mean:</span>
      {hints.map(h => (
        <Badge
          key={h.id}
          variant="outline"
          className="rounded-sm text-[11px] cursor-pointer hover:border-emerald-400 hover:bg-emerald-50"
          onClick={() => onPick && onPick(h)}
          title={[h.display_name, h.city, h.state].filter(Boolean).join(' · ')}
          data-testid={`${testIdPrefix}-chip-${h.id}`}
        >
          {h.customer_name}
          {h.city && <span className="ml-1 text-slate-400">· {h.city}</span>}
        </Badge>
      ))}
      <button
        className="text-slate-300 hover:text-slate-600 ml-1"
        onClick={() => setDismissed(true)}
        title="Dismiss"
        data-testid={`${testIdPrefix}-dismiss`}
      ><X className="w-3 h-3" /></button>
    </div>
  );
};

export default CustomerDedupHints;
